"use client";

import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { useRouter } from "next/navigation";
import { useClickOutside } from "primereact/hooks";
import { confirmPopup } from "primereact/confirmpopup";
import { InputTextarea } from "primereact/inputtextarea";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";

import EnhancedTable from "@/hocs/EnhancedTable";
import { useToastStore } from "@/stores/toast.store";
import { formatDateWithTimezone } from "@/utils/func";
import useTimezoneStore from "@/stores/timezone.store";
import { ApiResponse } from "@/interfaces/api-response";
import customizationFormApi from "@/api/customization-form";
import templateApi from "@/api/template";
import clipArtCategoryApi from "@/api/clip-art-category";
import aiFilterApi from "@/api/ai-filter";
import ActionCell from "@/components/CustomizationForm/ActionCell";
import CustomizationForm, { FunctionType } from "@/interfaces/customization-form.interface";
import CampaignBadges from "@/components/CustomizationForm/CampaignBadges";
import ResourceGrantDialog from "@/components/ResourceGrantDialog";
import { CONSTANTS_KEYS } from "@/constants/store";

const ROWS_PER_PAGE = 10;

const CustomizationFormsListPage = () => {
  const router = useRouter();
  const displayTimeZone = useTimezoneStore((s) => s.timezone);
  const addToast = useToastStore((s) => s.addToast);
  const [selectFormId, setSelectFormId] = useState<string | null>(null);
  const [sharingForm, setSharingForm] = useState<CustomizationForm | null>(null);
  const refetchRef = React.useRef<(() => void) | null>(null);
  const currentUser = useMemo(() => {
    try {
      const raw = Cookies.get(CONSTANTS_KEYS.USER);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);
  const hasCustomizationFormAll = currentUser?.isSuperAdmin === true || (
    (currentUser?.permissionCodes?.includes("customization_form:read:all") ?? false) &&
    (currentUser?.permissionCodes?.includes("customization_form:update:all") ?? false)
  );
  const canShare = (form: CustomizationForm) =>
    hasCustomizationFormAll || form.userId === currentUser?._id;

  const fetchCustomizationFormsWithoutElements = async (
    params: any,
  ): Promise<ApiResponse<CustomizationForm[]>> => {
    return await customizationFormApi.getMany({
      ...params,
      excludeElements: true,
    });
  };

  const handleCreateNew = async () => {
    try {
      const response = await customizationFormApi.createOne({
        name: "Untitled Form",
      });
      if (response.data) {
        addToast({
          severity: "success",
          detail: "New form created. Redirecting to editor...",
        });
        router.push(`/customization-forms/${response.data._id}`);
      }
    } catch (error) {
      console.error("Failed to create form:", error);
      addToast({
        severity: "error",
        detail: "Could not create a new form.",
      });
    }
  };

  const handleDelete = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    form: CustomizationForm,
  ) => {
    setSelectFormId(form._id);
    confirmPopup({
      target: event.currentTarget,
      message: (
        <>
          Are you sure you want to delete the form
          <p className="max-w-2xl">
            <strong>&quot;{form.name}&quot;?</strong>
          </p>
        </>
      ),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await customizationFormApi.deleteOne(form._id);
          addToast({
            severity: "success",
            detail: "Form deleted successfully",
          });
          refetchRef.current?.();
        } catch (error) {
          console.error("Failed to delete form:", error);
          addToast({ severity: "error", detail: "Failed to delete form" });
        }
      },
      reject: () => {},
      onHide: () => setSelectFormId(null),
    });
  };

  const handleClone = async (formId: string) => {
    try {
      const res = await customizationFormApi.cloneOne(formId);
      if (res.data) {
        addToast({ severity: "success", detail: "Form cloned successfully" });
      }
    } catch (error) {
      console.error("Failed to clone form:", error);
      addToast({ severity: "error", detail: "Failed to clone form" });
    } finally {
      refetchRef.current?.();
    }
  };

  const createButton = (
    <Button
      label="Create New Form"
      icon="pi pi-plus"
      onClick={handleCreateNew}
    />
  );

  return (
    <div className="relative flex h-full w-full flex-col rounded-lg bg-white p-6 shadow-lg">
      <EnhancedTable
        size="small"
        cachedKey="customization-forms"
        fetchData={fetchCustomizationFormsWithoutElements}
        itemsPerPage={ROWS_PER_PAGE}
        searchPlaceholder="Search forms by name or ID"
        title="Customization Forms"
        scrollable
        suffix={createButton}
        refetchRef={refetchRef}
        rowClassName={(rowData) => {
          return rowData._id === selectFormId ? "highlight-table-row" : "";
        }}
      >
        <Column
          field="name"
          header="Form Name"
          body={(rowData: CustomizationForm) => (
            <RenderFormName form={rowData} refetchRef={refetchRef} />
          )}
          style={{ minWidth: "400px" }}
        />

        <Column
          header="Linked Campaigns"
          body={(rowData: CustomizationForm) => (
            <CampaignBadges campaigns={rowData.campaigns} />
          )}
          style={{ minWidth: "180px" }}
        />

        <Column
          header="Timestamp"
          field="updatedAt"
          body={(rowData: CustomizationForm) => (
            <p className="flex flex-col items-center">
              <span
                className="text-gray-500"
                style={{
                  fontSize: "0.8rem",
                }}
              >
                {formatDateWithTimezone(rowData.createdAt, displayTimeZone)}
              </span>
              <span className="flex h-max w-full items-center justify-center">
                <i
                  className="pi pi-arrow-down text-sm text-gray-500"
                  style={{
                    fontSize: "0.5rem",
                  }}
                ></i>
              </span>
              <span
                className="text-gray-500"
                style={{
                  fontSize: "0.8rem",
                }}
              >
                {formatDateWithTimezone(rowData.updatedAt, displayTimeZone)}
              </span>
            </p>
          )}
          style={{ width: "140px", whiteSpace: "nowrap", textAlign: "center" }}
        />

        <Column
          header="Actions"
          body={(rowData: CustomizationForm) => {
            return (
              <ActionCell
                form={rowData}
                onClone={handleClone}
                onDelete={handleDelete}
                onShare={canShare(rowData) ? setSharingForm : undefined}
              />
            );
          }}
          style={{ width: "120px", textAlign: "right" }}
          headerStyle={{ textAlign: "right" }}
        />
      </EnhancedTable>
      <ResourceGrantDialog
        visible={Boolean(sharingForm)}
        resourceEndpoint="customization-forms"
        resourceType="customization_form"
        resourceId={sharingForm?._id}
        resourceName={sharingForm?.name}
        ownerId={sharingForm?.userId}
        relatedResourceLabel="linked resources"
        loadRelatedResources={async (formId) => {
          const functionResourceConfig: Partial<Record<FunctionType, {
            resourceEndpoint: string;
            resourceType: string;
            fetch: (id: string) => Promise<{ _id: string; name: string } | null>;
          }>> = {
            [FunctionType.ChangeTemplate]: {
              resourceEndpoint: "templates",
              resourceType: "template",
              fetch: async (id) => (await templateApi.getOneByIdNoToast(id))?.data ?? null,
            },
          };

          const response = await customizationFormApi.getOneById(formId);
          const form = response.data;
          if (!form?.elements?.length) return [];

          type ResourceRef = {
            resourceEndpoint: string;
            resourceType: string;
            id: string;
            fetch: (id: string) => Promise<{ _id: string; name: string } | null>;
          };
          const uniqueResourceRefs = new Map<string, ResourceRef>();
          const trackResourceRef = (
            resourceEndpoint: string,
            resourceType: string,
            id: string,
            fetch: (id: string) => Promise<{ _id: string; name: string } | null>,
          ) => {
            if (!id) return;
            const dedupeKey = `${resourceType}:${id}`;
            if (!uniqueResourceRefs.has(dedupeKey)) uniqueResourceRefs.set(dedupeKey, { resourceEndpoint, resourceType, id, fetch });
          };

          for (const element of form.elements) {
            for (const fn of element.functions ?? []) {
              const config = functionResourceConfig[fn.type];
              if (!config) continue;
              for (const val of fn.values ?? []) {
                if (val.mappingValue) trackResourceRef(config.resourceEndpoint, config.resourceType, val.mappingValue, config.fetch);
              }
            }

            if (element.libraryBinding?.categoryId) {
              trackResourceRef(
                "clip-art-categories",
                "clip_art_category",
                element.libraryBinding.categoryId,
                async (id) => (await clipArtCategoryApi.getOneById(id))?.data ?? null,
              );
            }

            if (element.typeConfig?.type === "photo_picker") {
              for (const step of element.typeConfig.config.processingSteps ?? []) {
                if (step.options?.filterId) {
                  trackResourceRef(
                    "ai-filters",
                    "ai_filter",
                    step.options.filterId,
                    async (id) => (await aiFilterApi.getOneById(id))?.data ?? null,
                  );
                }
              }
            }
          }

          if (!uniqueResourceRefs.size) return [];

          const fetchedResources = await Promise.all(
            [...uniqueResourceRefs.values()].map(async (resourceRef) => {
              const data = await resourceRef.fetch(resourceRef.id);
              return data ? { resourceRef, data } : null;
            }),
          );

          return fetchedResources
            .filter((item): item is NonNullable<typeof item> => Boolean(item?.data._id))
            .map(({ resourceRef, data }) => ({
              resourceEndpoint: resourceRef.resourceEndpoint,
              resourceType: resourceRef.resourceType,
              resourceId: data._id,
              name: data.name,
            }));
        }}
        onHide={() => setSharingForm(null)}
      />
    </div>
  );
};

export default CustomizationFormsListPage;

const RenderFormName = ({
  form,
  refetchRef,
}: {
  form: CustomizationForm;
  refetchRef: React.RefObject<(() => void) | null>;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [formName, setFormName] = useState("");
  const addToast = useToastStore((s) => s.addToast);

  useEffect(() => {
    setFormName(form.name);
  }, [form]);

  const handleClose = () => {
    setIsEditing(false);
  };

  const handleSave = () => {
    customizationFormApi
      .updateOneById(form._id, { name: formName })
      .then(() => {
        setIsEditing(false);
        setFormName(formName);
        addToast({
          severity: "success",
          detail: "Form name updated successfully",
        });
        refetchRef.current?.();
      });
  };

  useClickOutside(formRef as React.RefObject<Element>, handleClose);

  useEffect(() => {
    if (isEditing && textAreaRef.current) {
      textAreaRef.current.focus();
      const length = textAreaRef.current.value.length;
      textAreaRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  return (
    <div
      className="relative w-full rounded p-2 transition-all hover:bg-gray-100"
      ref={formRef}
    >
      {isEditing ? (
        <InputTextarea
          rows={3}
          ref={textAreaRef}
          cols={30}
          value={formName}
          onChange={(e) => setFormName(e.target.value)}
          className="w-full rounded border p-1"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSave();
            }
          }}
        />
      ) : (
        <span
          className="line-clamp-2 w-full cursor-pointer"
          onClick={() => setIsEditing(true)}
        >
          {formName}
        </span>
      )}
      <div
        className={`absolute right-1 bottom-1 gap-2 ${
          isEditing ? "flex" : "hidden"
        }`}
      >
        <i
          className="pi pi-undo cursor-pointer rounded-md bg-white p-2 text-black shadow hover:bg-gray-600 hover:text-white"
          onClick={handleClose}
        ></i>
        <i
          className="pi pi-save cursor-pointer rounded-md bg-white p-2 text-blue-600 shadow hover:bg-blue-600 hover:text-white"
          onClick={handleSave}
        ></i>
      </div>
    </div>
  );
};
